import {
  Address,
  AttachedBlobAsset,
  CurrencyCode,
  InputQuoteState,
  InputUpdateQuoteItems,
  Quote,
  QuoteAlternativeType,
  QuoteFinalStateReason,
  QuoteItem,
  QuotePresentation,
  QuotePrice,
  QuotePriceCalculation,
  QuoteSetSibling,
  QuoteState
} from '../../../api/dealer-api-interface-quote';
import {
  BranchQuoteSupportItemType,
  BranchQuoteSupportStatus,
  PaymentProfileCalculation,
  PurchaseOrderState,
  QuoteItemConversationType
} from '../../../api/dealer-api-interface-franchisee';
import { cache } from '../../cache-impl/cache-registry';
import {
  canAppearOnSupplierOrder,
  getQuoteNumberFormatted,
  isFrame,
  isQuoteDraft,
  isSpecialItem,
  isSupplierFrameItem,
  isSupplierSpecialItem,
  mustAppearOnPurchaseOrder
} from '../../../quotes/data/quote-helper-functions';
import { clone, compare } from '../../../../webmodule-common/other/clone';
import {
  concatNotes,
  firstValidString,
  flagInSet,
  isEmptyOrSpace,
  joinWithCommaAnd
} from '../../../../webmodule-common/other/ui/string-helper-functions';
import {
  currentUserClaims,
  isCynclyStaff,
  isSupplierAgent
} from '../../../../webmodule-common/other/currentuser-claims';
import { DateTime } from 'luxon';
import {
  determineProjectShippingAddress,
  getProjectNumberFormatted
} from '../../projects/data/project-helper-functions';
import { DevelopmentError } from '../../../../webmodule-common/other/development-error';
import { emptyGuid, newGuid } from '../../../../webmodule-common/other/api/guid';
import {
  fetchQuoteAsset,
  getV6FrameForContainer,
  getV6QuoteItemCache,
  getV6QuoteItemForContainer,
  isQuoteFrameVersionCurrent,
  isSupplierUnitsMetric,
  isSupplierUsingSSI,
  isV6
} from '../../../quotes/data/v6/helper-functions';
import {
  FrameAttribute,
  FrameAttributeGroupType,
  FrameCompareReport,
  FrameValidationType,
  getFrameCompareAsMarkdown,
  NullPromise,
  QCQuoteCalculationValues,
  QCQuoteValues,
  QuoteFrameBuyInItem,
  V6QuoteAnnotation,
  V6QuoteAttributesConfig
} from '../../../../webmodule-common/interop/webmodule-interop';
import { FranchiseeApi } from '../../../api/franchisee-api';
import { FranchiseeQuoteAddressManager, FranchiseeQuoteAddressManagerType } from './franchisee-quote-address-manager';
import { FranchiseeQuoteContainer } from './franchisee-quote-container';
import { FranchiseeQuoteUserInteraction } from './franchisee-quote-user-interaction';
import { getApiFactory } from '../../../api/api-injector';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import { getProjectContainerManager } from '../../projects/project-ui-container-manager';
import { getPurchaseOrderNumberFormatted } from '../../../purchase-orders/data/purchase-order-helper-functions';
import { getQuoteStateEngine } from './quote-state-engine';
import { getSessionLock } from '../../common/optimistic-lock';
import { highestValidationRank, rankToValidationType } from '../../../v6config/validation-rank';
import { IUserQuoteStateEngine } from '../../../quotes/data/quote-state-engine';
import {
  localDateTimeToServer,
  localDateToRFC3339,
  localDateToServer,
  serverDateTimeToLocalDateTime,
  today
} from '../../../../webmodule-common/other/datetime-converter';
import {
  MockSSIProcessor,
  QuoteContainer,
  QuoteContainerManager,
  StockLookupViewExtra
} from '../../../quotes/data/quote-container';
import { money } from '../../../../webmodule-common/other/currency-formatter';
import { ProjectState, ResourceType } from '../../../api/dealer-api-interface-project';
import { QuoteApi } from '../../../api/quote-api';
import { QuoteItemContainer } from '../../../quotes/data/quote-item-container';
import { QuoteProgressSession, QuoteReasonOption } from '../../../quotes/data/quote-user-interaction';
import { SSIProcessor } from '../../../quotes/data/ssi-processor';
import type { BlobApi } from '../../../api/blob-api';
import type {
  BranchQuote,
  BranchQuoteSupport,
  InputBoundConversationEntry,
  InputLineItem,
  LookupStockItem,
  PurchaseOrderBatchInput,
  QuoteItemConversation,
  ResultGetBranchQuoteSupport,
  ResultGetSupplierPricingRule,
  ResultGetSupplierQuoteSettings,
  SupportQuoteItemLinkDetail
} from '../../../api/dealer-api-interface-franchisee';

import { createSupportTicketService, SupportTicketService } from '../../conversation/support-ticket-service';
import { emptyAddress } from '../../../../webmodule-common/other/address-helpers';
import {
  type EventSourceHandler,
  stagedExecution
} from '../../../../webmodule-common/other/general/event-source-client';
import { fromJsonStr } from '../../../../webmodule-common/other/blob-converters';
import { glossary } from '../../../../localization/global';
import { LangFranchiseeQuoteDetailView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-detail-view-lang';
import { LangFranchiseeQuoteItemsView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-items-view-lang';
import { LangFranchiseeQuoteManager } from '../../../../localization/scripts/dealer-franchisee/quotes/data/franchisee-quote-manager-lang';
import { LangModalValidationHandler } from '../../../../localization/webmodule-common/other/ui/modal-validationhandler-lang';
import { LangQuoteStateReasons } from '../../../../localization/scripts/quotes/quote-state-reasons-lang';
import { monitorQuoteCopyCompletion } from './quote-copy-completion-monitor';
import { QuoteProviderData } from '../../../quotes/data/quote-provider-data';
import { sharedExecute } from '../../../../webmodule-common/other/common/helpers/callbacks';
import { userDataStore } from '../../common/current-user-data-store';
import { v6BootLoader } from '../../../v6config/v6config';
import {
  v6ConfigSupplierOptions,
  v6SupportsVersion,
  v6Util,
  v6VersionMap
} from '../../../../webmodule-common/interop/interfaces/v6-config';
import { V6FranchiseeQuoteProviderData } from './franchisee-quote-provider-data';
import { V6SSIProcessor } from '../../../quotes/data/v6/v6-ssi-processor';
import { V6SupplierQuoteBase } from '../../../quotes/data/v6/v6-supplier-quote';
import { WMEventSource } from '../../../api/event-source';
import type { QuoteItemFabricationStatusView, SSEQuoteFabricationStatus } from '../../../api/fabrication-job-api';

// UI intent events emitted by FranchiseeQuoteContainerManager to decouple business logic
// from direct browser location / DOM mutations. Consumers (shell/router components) subscribe
// and perform side effects like updating hash or focusing panels.
export type FranchiseeQuoteManagerUiEvent =
  | { type: 'show-fragment'; fragment: string; source?: string }
  | { type: 'support-items-needed'; count: number };

export function mapPaymentProfileCalculation(paymentProfile: PaymentProfileCalculation): QuotePriceCalculation {
  switch (paymentProfile) {
    case PaymentProfileCalculation.Margin:
      return QuotePriceCalculation.Margin;
    case PaymentProfileCalculation.Markup:
      return QuotePriceCalculation.Markup;
    default:
      throw new Error('Invalid PaymentProfileCalculation value');
  }
}

export class FranchiseeQuoteContainerManager extends QuoteContainerManager {
  /**
   * Set by the page that OWNS the interactive lock (app-quote), so this manager can hand the quote
   * over when it copies the quote it is holding open — see the handover in internalMakeCopy.
   *
   * A callback rather than a direct call because the lock lives on the page
   * (`app-resource-base.this.lock`) while the copy is driven from here; the same shape as the
   * `lockRevokedEvent` / `concurrencyCheck` callbacks app-quote already passes into QuoteLock.
   * Undefined for managers that hold no lock (the quote list and project views), where standing down
   * would be meaningless.
   */
  standDownFromEditing?: () => Promise<void>;

  // --- Lightweight internal event bus (UI decoupling from direct hash mutation) ---
  private _uiListeners: Set<(evt: FranchiseeQuoteManagerUiEvent) => void> = new Set();
  /** Subscribe to UI intent events (e.g., request to show support panel). Returns unsubscribe. */
  onUiEvent(listener: (evt: FranchiseeQuoteManagerUiEvent) => void): () => void {
    this._uiListeners.add(listener);
    return () => this._uiListeners.delete(listener);
  }
  /** Publish a UI intent event without performing DOM/location side effects inside manager. */
  private publishUi(evt: FranchiseeQuoteManagerUiEvent): void {
    for (const l of Array.from(this._uiListeners)) {
      try {
        l(evt);
      } catch (err) {
        // swallow listener errors to avoid blocking other listeners
      }
    }
  }
  async hasActiveSupportTickets(id: string): Promise<{ success: boolean; items: BranchQuoteSupport[] }> {
    await this.needsQuoteSupport();
    const items = this.branchQuoteSupport.items.filter(
      x =>
        x.quoteItemLink &&
        (x.quoteItemLink[id]?.isLinked ?? false) &&
        flagInSet(
          x.status,
          BranchQuoteSupportStatus.New ||
            BranchQuoteSupportStatus.Active ||
            BranchQuoteSupportStatus.Draft ||
            BranchQuoteSupportStatus.Overdue ||
            BranchQuoteSupportStatus.ReActivated
        )
    );
    if (!items || items.length === 0) return { success: false, items: [] };

    return { success: true, items };
  }
  private quoteItemConversationLinks: QuoteItemConversation[] = [];
  registerQuoteItemConversationLink(quoteItemConversationLink: QuoteItemConversation | null | undefined) {
    if (quoteItemConversationLink) {
      this.quoteItemConversationLinks.push(quoteItemConversationLink);
    }
  }
  quoteItemLinkFor(quoteItemId: string) {
    return this.quoteItemConversationLinks.filter(x => x.quoteItemId === quoteItemId);
  }
  unregisterQuoteItemConversationLink(quoteItemConversationLink: QuoteItemConversation | null | undefined) {
    if (quoteItemConversationLink) {
      this.quoteItemConversationLinks = this.quoteItemConversationLinks.filter(
        link => link !== quoteItemConversationLink
      );
    }
  }
  franchiseeApi: FranchiseeApi;
  /** Same object as `interaction`, retyped for the franchisee-only project-setup step. */
  private readonly franchiseeInteraction: FranchiseeQuoteUserInteraction;
  protected userClaims = currentUserClaims();
  /** Progress display for a multi-step item copy, held between the before/finally hooks. */
  private copyProgress?: QuoteProgressSession;
  /** Instance-scoped support ticket service (replaces prior global singleton). */
  public readonly supportTicketService: SupportTicketService;

  constructor(
    original: QuoteContainer,
    quoteApi: QuoteApi,
    blobApi: BlobApi,
    franchiseeApi: FranchiseeApi,
    interaction: FranchiseeQuoteUserInteraction
  ) {
    super(original, quoteApi, blobApi, interaction);
    this.franchiseeInteraction = interaction;
    if (!(original instanceof FranchiseeQuoteContainer))
      throw new DevelopmentError('FranchiseeQuoteContainerManager only supports FranchiseeQuoteContainer');
    this.franchiseeApi = franchiseeApi;
    this.supportTicketService = createSupportTicketService();
    // Initialize quote context for support ticket service
    this.updateSupportServiceQuoteContext();
  }
  updateSupportServiceQuoteContext() {
    if (this.container.quote?.quoteSetId && this.container.quote.id) {
      this.supportTicketService.setQuoteContext(this.container.quote.quoteSetId, this.container.quote.id);
    }
  }

  get franchiseeContainer(): FranchiseeQuoteContainer {
    return this.container as FranchiseeQuoteContainer;
  }

  get franchiseeBackup(): FranchiseeQuoteContainer {
    return this.backup as FranchiseeQuoteContainer;
  }

  get quoteCurrency(): CurrencyCode {
    return this.container.quote?.quoteCurrency ?? CurrencyCode.USD;
  }

  get hasQuoteCalculationData(): boolean {
    return this.quoteCalcuationData !== undefined;
  }

  /**
   * The quote-level calculation groups that have something to show, in supplier order.
   *
   * Deliberately narrower than {@link hasQuoteCalculationData}, which only says a calculation
   * payload arrived at all. That payload carries two independent arrays: `quoteItemValues`
   * (per-item, drives the extra V6 item-table columns) and `quoteValues` (the groups). A
   * supplier can return the former with none of the latter, and can return a titled group
   * carrying no values, so anything that renders the groups must go through this - otherwise
   * it renders an empty page, or a title bar over blank space.
   */
  get quoteCalculationValues(): QCQuoteCalculationValues[] {
    const data = this.quoteCalcuationData as QCQuoteValues | undefined;
    return data?.quoteValues?.filter(group => (group.values?.length ?? 0) > 0) ?? [];
  }

  get hasQuoteCalculationValues(): boolean {
    return this.quoteCalculationValues.length > 0;
  }

  get quoteCalcuationData(): QCQuoteValues {
    const data = this.quoteProviderData() as V6FranchiseeQuoteProviderData;
    return (data.quoteCalculations as QCQuoteValues) ?? undefined;
  }

  get hasQuoteAnnotations(): boolean {
    return this.quoteAnnotations.length > 0;
  }

  get quoteAnnotations(): V6QuoteAnnotation[] {
    const data = this.quoteProviderData() as V6FranchiseeQuoteProviderData;
    return (data.quoteAnnotations as V6QuoteAnnotation[]) || [];
  }

  get hasQuoteSupport(): boolean {
    const snap = this.supportTicketService.getState().ticketLists[this.quote.id];
    return !!snap && snap.items.length > 0;
  }

  get branchQuote(): BranchQuote {
    if (!this.franchiseeContainer.branchQuote) {
      throw new DevelopmentError('BranchQuote is null'); //no waiting
    }
    return this.franchiseeContainer.branchQuote;
  }

  public get isAnyStateChangeAllowed(): boolean {
    const stateEngine = this.getStateEngine();
    return !this.isLockedFromUse() && stateEngine.isAnyStateChangeAllowed();
  }

  public get currentStateRequiresValidation(): boolean {
    switch (this.quoteState) {
      case QuoteState.Approved:
      case QuoteState.Lapsed:
      case QuoteState.Cancelled:
      case QuoteState.Rejected:
      case QuoteState.Accepted_RefusedBySupplier:
        return false;
      default:
        return true;
    }
  }

  public get isAnyStateChangeAllowedThatRequiresValidation(): boolean {
    const stateEngine = this.getStateEngine();
    return (
      !this.isLockedFromUse() &&
      stateEngine.isAnyStateChangeAllowed([
        QuoteState.Active,
        QuoteState.IssuePending,
        QuoteState.Issued,
        QuoteState.Accepted,
        QuoteState.SupplierReviewPending,
        QuoteState.SupplierReviewed,
        QuoteState.Approved,
        QuoteState.Accepted_AssignedToReviewer
      ])
    );
  }

  public get isValidationOnLockedQuote(): boolean {
    const stateEngine = this.getStateEngine();
    return (
      this.isReadonly() &&
      !this.isLockedFromUse() &&
      (stateEngine.isStateChangeAllowed(QuoteState.Accepted) ||
        stateEngine.isStateChangeAllowed(QuoteState.Approved) ||
        stateEngine.isStateChangeAllowed(QuoteState.SupplierReviewPending))
    );
  }

  public get branchQuoteSupport(): ResultGetBranchQuoteSupport {
    const snap = this.supportTicketService.getState().ticketLists[this.quote.id];
    if (snap) {
      return {
        items: snap.items,
        masterDocuments: snap.masterDocuments,
        conversationLinks: snap.conversationLinks
      };
    }
    return { conversationLinks: [], items: [], masterDocuments: [] };
  }

  /**
   * Items genuinely need re-validating: one or more frames are out of date. Deliberately does NOT include
   * deferred pricing — see {@link pricingMustBeUpdated}. Folding the two together told the user to press
   * Validate for a condition Validate neither inspects nor clears, so they were sent to a button that
   * reported nothing wrong and left them stuck.
   */
  public async currentUserMustValidate(): Promise<boolean> {
    return (
      !this.isLockedFromUse() &&
      (this.currentStateRequiresValidation || this.isAnyStateChangeAllowedThatRequiresValidation) &&
      (await this.anyFrameOutOfDate([]))
    );
  }

  /**
   * Deferred SSI (Task 230006): a batch change parked the supplier-item/pricing recompute, so pricing is
   * stale until the user runs Update Price. Blocks the same actions validation blocks — the guard is
   * unchanged — but it is reported separately because the remedy is a different button.
   */
  public get pricingMustBeUpdated(): boolean {
    return (
      !this.isLockedFromUse() &&
      (this.currentStateRequiresValidation || this.isAnyStateChangeAllowedThatRequiresValidation) &&
      this.ssiDeferred
    );
  }

  /**
   * The display number as the purchase order records it (an int column). Numbers are strings on the quote
   * because a future numbering scheme may not be numeric; anything non-numeric is recorded as "not
   * captured" rather than coerced, so the order falls back to its quote-wide ordinal instead of printing
   * a number nobody chose.
   */
  private toRecordedNumber(displayNumber: string | undefined): number | null {
    if (displayNumber === undefined) return null;
    const parsed = Number.parseInt(displayNumber, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  public get allowNonTaxableItems(): boolean {
    return this.container.quote?.allowNonTaxableItems ?? userDataStore.config.allowNonTaxableItems;
  }

  getStateEngine(): IUserQuoteStateEngine {
    return getQuoteStateEngine(this);
  }

  async fetchCache() {
    await Promise.allSettled([
      cache().quote.preFetch([this.quoteId]),
      cache().projectResourceLink.preFetch([this.quoteId])
    ]);
  }

  async createBranchQuote() {
    await userDataStore.loadCoreDetails();
    if (!userDataStore.defaultBranch) throw new Error(LangFranchiseeQuoteManager.currentUserDoesNotHaveBranch());
    const branchQuote: BranchQuote = {
      branchId: userDataStore.defaultBranch.id,
      globalSupplierSupplierId: this.quote.supplierId,
      quoteSetId: this.quote.quoteSetId,
      //Use the exact same id for the branch quote. makes it easy to extend
      id: this.quote.id,
      clientId: emptyGuid,
      clientName: '',
      clientTypeId: emptyGuid,
      contactId: emptyGuid,
      clientTypeName: '',
      contactName: '',
      dateCreated: localDateTimeToServer(new Date()),
      recordVersion: '',
      lockForFabrication: false
    };

    //if there is any reason we can't create this, we won't worry about it now, if we already have the quote
    //created. we will just attempt to create it later. if it cant be done later, it is probably
    //a system failure.
    const branchQuoteResult = await this.franchiseeApi.createBranchQuote({
      branchQuote: branchQuote
    });
    if (!branchQuoteResult) return null;
    return branchQuoteResult.branchQuote;
  }

  async needsQuoteSupport(force?: boolean): NullPromise<ResultGetBranchQuoteSupport> {
    await this.needsQuote();
    const service = this.supportTicketService;
    if (force) {
      return (
        (await service.loadTicketList({ force: true })) ?? { conversationLinks: [], items: [], masterDocuments: [] }
      );
    }
    const snap = service.getState().ticketLists[this.quote.id];
    if (snap) {
      return {
        items: snap.items,
        masterDocuments: snap.masterDocuments,
        conversationLinks: snap.conversationLinks
      };
    }
    return (
      (await service.loadTicketList({ force: false })) ?? { conversationLinks: [], items: [], masterDocuments: [] }
    );
  }

  // --- SupportTicketService integration (direct state access) ---
  /** @deprecated No longer needed - manager reads directly from service state */
  public attachSupportService(): void {
    // No-op: Manager now accesses service state directly via branchQuoteSupport getter
  }
  /** @deprecated No longer needed - manager reads directly from service state */
  public detachSupportService(): void {
    // No-op: No subscription to clean up
  }

  // --- Quote item fabrication status cache + SSE ---
  // Loaded once via needsItemFabricationSummaryData (called from refreshData),
  // then kept fresh exclusively via SSE events from all fabrication mutations.
  private _itemFabricationStatuses = new Map<string, QuoteItemFabricationStatusView>();
  private _fabStatusLoaded = false;
  private _fabStatusGeneration = 0;
  private _fabricationSseAttached = false;
  private _fabricationSseHandler?: EventSourceHandler;
  private _fabricationStatusListeners = new Set<() => void>();

  public get itemFabricationStatuses(): ReadonlyMap<string, QuoteItemFabricationStatusView> {
    return this._itemFabricationStatuses;
  }

  // Load-once: safe to call from refreshData() repeatedly — no-op after first load.
  // sharedExecute deduplicates concurrent initial-load calls.
  public needsItemFabricationSummaryData = sharedExecute(this._needsItemFabricationSummaryData.bind(this));
  private async _needsItemFabricationSummaryData(): Promise<void> {
    if (!userDataStore.isFabricatorMode) return;
    if (this._fabStatusLoaded) return;
    await this._fetchFabricationStatuses();
    this._fabStatusLoaded = true;
  }

  // Force re-fetch — use after user-initiated mutations (e.g. assigning item to job)
  // where we can't wait for the SSE round-trip.
  public async refreshItemFabricationStatuses(): Promise<void> {
    if (!userDataStore.isFabricatorMode) return;
    await this._fetchFabricationStatuses();
  }

  private async _fetchFabricationStatuses(): Promise<boolean> {
    const gen = ++this._fabStatusGeneration;
    if (!this.container.items) return false;
    const ids: string[] = [
      ...this.container.items.map(qi => qi.id),
      ...(this.container.deletedItems?.map(qi => qi.id) ?? [])
    ];
    if (ids.length === 0) {
      if (gen !== this._fabStatusGeneration) return false;
      this._itemFabricationStatuses.clear();
      this._notifyFabricationStatusListeners();
      return true;
    }
    try {
      const result = await getApiFactory().fabricationJob().getQuoteItemFabricationStatuses({ quoteItemIds: ids });
      if (gen !== this._fabStatusGeneration) return false;
      this._itemFabricationStatuses.clear();
      result?.statuses?.forEach(s => this._itemFabricationStatuses.set(s.quoteItemId, s));
      this._notifyFabricationStatusListeners();
      return true;
    } catch {
      return false;
    }
  }

  public onFabricationStatusesChanged(cb: () => void): () => void {
    this._fabricationStatusListeners.add(cb);
    return () => this._fabricationStatusListeners.delete(cb);
  }

  private _notifyFabricationStatusListeners(): void {
    this._fabricationStatusListeners.forEach(cb => {
      try {
        cb();
      } catch {
        /* swallow listener errors */
      }
    });
  }

  public attachFabricationStatusSse(): void {
    if (!userDataStore.isFabricatorMode) return;
    if (this._fabricationSseAttached) return;

    let needsRefresh = false;

    const handler = stagedExecution({
      threshold: 250,
      testInterval: 100,
      title: 'QuoteFabricationStatus',

      eventActioned: (data: any) => {
        const payload = data as SSEQuoteFabricationStatus | undefined;
        if (!payload?.quoteItemIds?.length) return;
        const relevantIds = new Set<string>([
          ...(this.container.items?.map(qi => qi.id) ?? []),
          ...(this.container.deletedItems?.map(qi => qi.id) ?? [])
        ]);
        if (payload.quoteItemIds.some(id => relevantIds.has(id))) {
          needsRefresh = true;
        }
      },

      event: async () => {
        if (!needsRefresh) return;
        needsRefresh = false;
        try {
          const updated = await this._fetchFabricationStatuses();
          if (updated) {
            this.interaction.notifyInformation(LangFranchiseeQuoteItemsView.fabricationStatusUpdated());
          }
        } catch {
          /* prevent unhandled rejection */
        }
      }
    });

    WMEventSource.getInstance().addEventListener(WMEventSource.quoteFabricationStatus, handler);
    this._fabricationSseHandler = handler;
    this._fabricationSseAttached = true;
  }

  public detachFabricationStatusSse(): void {
    if (!this._fabricationSseAttached || !this._fabricationSseHandler) return;
    try {
      WMEventSource.getInstance().removeEventListener(
        WMEventSource.quoteFabricationStatus,
        this._fabricationSseHandler
      );
    } catch {
      /* ignore */
    }
    this._fabricationSseAttached = false;
    this._fabricationSseHandler = undefined;
    this._fabricationStatusListeners.clear();
  }

  public needsQuote = sharedExecute(this._needsQuote.bind(this));
  protected async _needsQuote(): Promise<boolean> {
    if (!this.container.quote) {
      const result = await this.api.getQuote({ quoteId: this.quoteId });

      if (result) {
        if (!result.quote.serviceProviderData || result.quote.serviceProviderData === '') {
          const dataPath = result.quote.assetVirtualPaths['data'];
          if (dataPath) {
            result.quote.serviceProviderData = (await fetchQuoteAsset({
              quoteId: this.quoteId,
              assetPath: dataPath.path,
              publicUrl: dataPath.publicUrl
            })) as string | null;
          }
        }
        this.container.quoteProviderData = fromJsonStr(result.quote.serviceProviderData) ?? null;

        result.quote.serviceProviderData = null;
      }

      let branchQuote =
        (
          await this.franchiseeApi.getBranchQuotes({
            branchQuoteIds: [this.quoteId]
          })
        )?.branchQuotes[0] ?? null;
      //we will build a new branch quote on the fly.
      if (!branchQuote) branchQuote = await this.createBranchQuote();

      if (result && branchQuote) {
        await this.resetBranchQuote(
          result.quote,
          result.quotePrice,
          result.quotePresentation,
          branchQuote,
          result.siblings,
          this.container.quoteProviderData
        );

        await this.loadSupplierName();
        this.supportTicketService.supplierName = this.supplierName;
        await this.fetchCache();
        await this.updatePricingRules();
        await this.updateV6QuoteDefaults();
        this.updateSupportServiceQuoteContext();
      } else return false;
    }
    return true;
  }

  async shouldCreatePurchaseOrder() {
    const items = await this.itemsForPurchaseOrder();
    // if we end up with free hand items and/or shipping only, we should not create a purchase order
    const shouldCreatePO = items.some(item => mustAppearOnPurchaseOrder(item));
    return shouldCreatePO;
  }

  async itemsForPurchaseOrder() {
    await this.needsQuoteItems(true);
    return this.container.items?.filter(item => canAppearOnSupplierOrder(item)) ?? [];
  }

  async createPurchaseOrder() {
    const projectApi = getApiFactory().project();
    const purchaseOrderApi = getApiFactory().purchaseOrder();
    const prlCache = cache().projectResourceLink;
    const purchaseOrderCache = cache().purchaseOrder;
    const priceForItem = (item: QuoteItem) => this.container.itemPrices?.find(x => x.id === item.id);

    if (!(await this.shouldCreatePurchaseOrder())) return true;

    const items = await this.itemsForPurchaseOrder();

    const quoteProjectResourceLink = await prlCache.getData(this.quoteId);
    if (!quoteProjectResourceLink)
      throw new DevelopmentError(`Quote: ${this.quote.id} missing project resource link in cache`);
    //Maybe we can use the project cache here?
    const projectResult = await projectApi.getProject({
      projectId: quoteProjectResourceLink?.projectId ?? emptyGuid
    });

    //We should not get an empty project since the project would have been created earlier in the workflow (when
    //a quote is issued)
    if (projectResult === null) throw new DevelopmentError(`Invalid State. Quote ${this.quoteId} missing Project`);

    // The number the quote showed against each line, snapshotted onto the order (MRD, Task 230006) so the
    // order can always print what the dealer saw. Counted over EVERY item in the section, not just the
    // ones being ordered - that is the whole point: a batch of quote items 1, 2, 3 where only 1 and 3 go
    // to this supplier must order as 1 and 3.
    //
    // The grouping document IS the numbering authority, so it has to be loaded before asking: unloaded,
    // itemDisplayNumbers falls back to flat 1..N, and these numbers are recorded permanently - a batched
    // quote would pin the wrong ones forever. This path used to avoid loading it on purpose, because reading
    // it here once produced orders carrying batch ids but no batch rows. That hazard was about sourcing
    // batch METADATA from a document that might be empty; the batches below come from the DB rows loaded
    // with the items summary, and this load is a plain in-memory read that never saves.
    await this.needsItemGrouping();
    const displayNumbers = this.itemDisplayNumbers();

    // The purchase order records the batch's real Id, not its CommonId - the same rule the item and the quote
    // follow (PurchaseOrderItem.QuoteItemId = item.Id, QuoteCommonId = Quote.Id), and the identity V6 is now
    // given. It has to match, because the V6 sync-back echoes that identity and the poller upserts
    // PurchaseOrderBatch by it: recording a CommonId here while V6 reports Ids would mean the committed
    // delivery dates and status never land on the requested row.
    //
    // Client membership is CommonId-space (the grouping document's group node id IS the batch CommonId), so
    // this is the one place on the client that translates - the mirror of the converter doing it on the server.
    const batchPkByCommonId = new Map(this.quoteItemBatches.map(b => [b.commonId, b.id]));
    const batchPkFor = (commonId: string | null): string | null =>
      commonId ? (batchPkByCommonId.get(commonId) ?? null) : null;

    // we have at least one special item, so proceed with the PO creation
    //convert quote items to references
    const lineItems: InputLineItem[] =
      items.map(item => {
        const price = priceForItem(item);
        return {
          id: item.id,
          grossQuantityCost: price?.supplierNettQuantityCost ?? 0,
          grossSupplierQuantityCost: price?.supplierGrossQuantityCost ?? 0,
          supplierPriceAdjustment: price?.supplierPriceAdjustmentEnabled ? (price?.supplierPriceAdjustment ?? 0) : 0,
          // Snapshot the item's batch membership onto the PO (MRD, Task 230006), as the batch's Id - see
          // batchPkFor. Null when ungrouped, or when the batch row is unknown (nothing to correlate to).
          quoteItemBatchId: batchPkFor(this.itemBatchId(item.commonId)),
          // The quote's own number for this line, recorded for EVERY line - batched, ungrouped or a charge.
          // Null only if the presentation somehow never placed the item, in which case the order falls back
          // to its quote-wide ordinal.
          quoteItemBatchPosition: this.toRecordedNumber(displayNumbers.get(item.commonId))
        };
      }) ?? [];

    // Mirror the quote's delivery batches onto the PO so the PO domain is self-contained (MRD, Task 230006).
    // Only the batches an ordered item actually belongs to are worth carrying.
    //
    // BOTH sides come from the DB: membership from the link map and metadata from the QuoteItemBatch rows,
    // each loaded by the items summary that itemsForPurchaseOrder() just forced. This deliberately does NOT
    // read the grouping document - it is loaded lazily by the items view, so ordering a quote without having
    // opened that view left it null and produced a PO whose items carried batch ids but which had no batch
    // rows at all. The DB rows are the source of truth for batch metadata anyway; the document only owns
    // display order, which the PO does not use.
    // Batch ORDER follows the quote's display order, not the batch table's creation order: take each batch
    // the first time an ordered line item references it. lineItems is already in quote display order (the
    // items came from itemsForPurchaseOrder -> the sorted container), and the DB stores no batch sort order
    // by design - the presentation owns it. Reading this.quoteItemBatches directly would order by
    // DateCreated, so a batch the user later moved would appear on the PO in the wrong place.
    const batchOrder: string[] = [];
    for (const lineItem of lineItems) {
      const id = lineItem.quoteItemBatchId;
      if (id && id !== emptyGuid && !batchOrder.includes(id)) batchOrder.push(id);
    }

    // Keyed by PK, because batchOrder now collects the PKs the line items carry.
    const batchByPk = new Map(this.quoteItemBatches.map(b => [b.id, b]));
    const batches: PurchaseOrderBatchInput[] = [];
    for (const batchId of batchOrder) {
      const b = batchByPk.get(batchId);
      if (!b) continue;
      batches.push({
        // The batch's real Id. CommonId is presentation-only and never leaves Dealer: it is the group node id
        // in the grouping document, and it repeats across copies - so it cannot identify a batch to the
        // supplier or correlate a purchase order row.
        quoteItemBatchId: b.id,
        // Record the display position explicitly. batchOrder is already in quote display order, but the
        // order it is sent in cannot survive the round trip on its own: the rows are all written with the
        // same DateCreated, so the read has nothing to sort by unless we store one.
        sortOrder: batches.length,
        description: b.description,
        deliveryDate: b.deliveryDate,
        customerRef: b.customerRef,
        comment: b.comment,
        status: b.status
      });
    }

    const description = concatNotes(projectResult.project.description, this.quote.description);
    const isSupplierIssuingOrder =
      currentUserClaims().isAgent && this.quote.supplierAuthorizedToIssueOrderOnQuoteApproval;

    const purchaseOrderResult = await purchaseOrderApi.createPurchaseOrder({
      numberSeed: this.quote.quoteOwnerId,
      purchaseOrder: {
        state: isSupplierIssuingOrder ? PurchaseOrderState.IssuedPending : PurchaseOrderState.Draft,
        purchaseOrderNumber: 0,
        id: newGuid(),
        branchQuoteId: this.branchQuote.id,
        creationUserId: emptyGuid,
        dateCreated: localDateTimeToServer(today()),
        calculatedAdjustmentTotal: 0,
        calculatedNetTotal: 0,
        installationDate: this.quote.installationDate,
        supplierSystemLastModifiedDate: null,
        supplierSystemClosedDate: null,
        supplierSystemDeliveryDate: null,
        supplierSystemShippingLeadTime: null,
        supplierSystemStatus: null,
        recordVersion: '',
        description: description,
        title: `${this.quoteTitle}`,
        reference: '',
        customPurchaseOrderNumber: this.quote.customQuoteNumber,
        supplierId: this.quote.supplierId,
        lastModifiedDate: null,
        lastModifiedUserId: emptyGuid,
        assignedToUserId: this.quote.assignedToUserId,
        purchaseOrderCurrency: this.quote.quoteCurrency
      },
      lineItems: lineItems,
      batches: batches.length > 0 ? batches : null
    });

    if (!purchaseOrderResult) {
      this.interaction.notifyError(LangFranchiseeQuoteManager.unableToCreatePurchaseOrder());
      return false;
    } else {
      if (purchaseOrderResult.purchaseOrder.state === PurchaseOrderState.IssuedPending) {
        const success = await V6SupplierQuoteBase.issuePurchaseOrder(
          this.branchQuote.branchId,
          this.quote.id,
          projectResult.project.id,
          purchaseOrderResult.purchaseOrder.id
        );
        if (!success) {
          purchaseOrderResult.purchaseOrder.state = PurchaseOrderState.Draft;
          let bFailed = false;
          try {
            await purchaseOrderApi.updatePurchaseOrder({
              purchaseOrder: purchaseOrderResult.purchaseOrder,
              stateChangeReason: null
            });
          } catch {
            bFailed = true;
          }
          const msg = bFailed
            ? LangFranchiseeQuoteManager.issueOrderBySupplierFailed({
                poId: purchaseOrderResult.purchaseOrder.id,
                tenantId: getCurrentUser()?.tenantId.toString() ?? ''
              })
            : LangFranchiseeQuoteManager.issueOrderBySupplierFailedRecovered();
          await this.interaction.inform(msg, LangFranchiseeQuoteManager.errorProcessingPurchaseOrder());
        }
      }

      await purchaseOrderCache.updateLocal(purchaseOrderResult.purchaseOrder.id);
      this.interaction.notifySuccess(
        LangFranchiseeQuoteManager.purchaseOrderCreated({
          poNumber: getPurchaseOrderNumberFormatted(purchaseOrderResult.purchaseOrder)
        }),
        1000
      );
      await projectApi.createProjectResourceReference({
        resourceId: purchaseOrderResult.purchaseOrder.id,
        projectId: projectResult.project.id,
        typeOf: ResourceType.PurchaseOrder,
        parentResourceId: this.quote.id,
        parentTypeOf: ResourceType.Quote
      });
      return true;
    }
  }

  public async purchaseOrderId(): Promise<string> {
    if (!this.quote) return emptyGuid;
    const prlCache = cache().projectResourceLink;
    const data = await prlCache.getData(this.quote.id);
    return data?.purchaseOrderId ?? emptyGuid;
  }

  public async getInstallationAddress(): Promise<Address | null> {
    const id = await this.projectId();
    if (isEmptyOrSpace(id)) return null;
    const proj = await cache().project.getData(id);
    return proj?.projectSummary.installationAddress ?? null;
  }

  public async projectId(): Promise<string> {
    if (!this.quote) return emptyGuid;
    const prlCache = cache().projectResourceLink;
    const data = await prlCache.getData(this.quote.id);
    return data?.projectId ?? emptyGuid;
  }

  public isBuyInPriceCurrent(qic: QuoteItemContainer): boolean {
    if (this.priceValidation && this.priceValidation.length !== 0) {
      return this.priceValidation.findIndex(x => x.id === qic.item.id) < 0;
    }

    return true;
  }

  isFrameOutOfDate(quoteItemContainer: QuoteItemContainer, errors: string[], includeBuyIn = true): boolean {
    if (!(isV6(quoteItemContainer) && isSupplierFrameItem(quoteItemContainer.item))) return false;
    let outOfDate = false;

    if (!getV6QuoteItemCache(quoteItemContainer)) {
      //the data is corrupt. we are correcting for an invalid or corrupt data scenarion
      //we dont want to try and validate this kind of data.
      return false;
    }

    if (!isQuoteFrameVersionCurrent(quoteItemContainer)) {
      errors.push(
        LangFranchiseeQuoteManager.frameOutOfDateNeedsUpdate({
          position: this.itemPosition(quoteItemContainer.item.id)!
        })
      );
      outOfDate = true;
    }
    if (!this.isBuyInPriceCurrent(quoteItemContainer) && includeBuyIn) {
      errors.push(
        LangFranchiseeQuoteManager.frameOutOfDateNeedsUpdateBuyIn({
          position: this.itemPosition(quoteItemContainer.item.id)!
        })
      );
      outOfDate = true;
    }
    if (this.quoteAttributesMismatch(quoteItemContainer)) {
      errors.push(
        LangFranchiseeQuoteManager.frameOutOfDateNeedsUpdateQuoteAttributes({
          position: this.itemPosition(quoteItemContainer.item.id)!
        })
      );
      outOfDate = true;
    }

    if (this.quoteItemIguMismatch(quoteItemContainer)) {
      errors.push(
        LangFranchiseeQuoteManager.frameOutOfDateNeedsUpdateIGU({
          position: this.itemPosition(quoteItemContainer.item.id)!
        })
      );
      outOfDate = true;
    }
    return outOfDate;
  }
  quoteItemIguMismatch(quoteItemContainer: QuoteItemContainer) {
    const data = this.quoteProviderData() as V6FranchiseeQuoteProviderData;
    const v6QuoteItem = getV6QuoteItemForContainer(quoteItemContainer);
    if (!v6QuoteItem) return false; //Data Corrupt.
    return !(v6Util().isQuoteIguReferenceValid?.(v6QuoteItem, data.igu as object) ?? true);
  }

  quoteAttributesMismatch(quoteItemContainer: QuoteItemContainer): boolean {
    if (!v6SupportsVersion(v6VersionMap.hasQuoteDefaultConversion)) return false;
    const attributeConfig = this.getV6QuoteAttributesConfig() ?? [];
    function attributeIsDifferent(attr1: FrameAttribute, attr2?: FrameAttribute): boolean {
      const config = attributeConfig.triggers.find(x => x.code === attr1.code);
      if (config?.excludeFromValidation) return false; //this attribute is excluded from validation

      if (!attr2) return true;
      if (attr2.value !== attr1.value) return true;

      return false;
    }

    const data = this.quoteProviderData() as V6FranchiseeQuoteProviderData;
    const f = getV6FrameForContainer(quoteItemContainer);
    const quotedefaults = v6Util().getQuoteDefaults(data.defaultOptions as object);
    if (!f || !quotedefaults || quotedefaults.length === 0) return false;
    const quoteItemStuff = f.attributeGroups.find(x => x.groupType === FrameAttributeGroupType.Quote);
    const quoteStuff = quotedefaults.find(group => group.groupType === FrameAttributeGroupType.Quote);
    //no attributes in either
    if (!quoteItemStuff && !quoteStuff) return false;
    //one side has attributes and the other does not, must validate
    if (!quoteItemStuff || !quoteStuff) return true;

    //both sides have attributes, do a compare

    //check all items in quote to see if they exist and match in quoteItem
    if (
      quoteStuff.attributes.some(attr =>
        attributeIsDifferent(
          attr,
          quoteItemStuff.attributes.find(attr1 => attr1.code === attr.code)
        )
      )
    )
      return true;

    //check all items in quoteitem to see if they exist and match in quote
    if (
      quoteItemStuff.attributes.some(attr =>
        attributeIsDifferent(
          attr,
          quoteStuff.attributes.find(attr1 => attr1.code === attr.code)
        )
      )
    )
      return true;

    return false;
  }

  quoteDefaultsComparison(poldDefaults?: object, pnewDefaults?: object): FrameAttribute[] {
    if (!v6SupportsVersion(v6VersionMap.hasQuoteDefaultConversion)) return [];

    // Helper to compare two attributes
    function getChangedAttribute(oldAttr?: FrameAttribute, newAttr?: FrameAttribute): FrameAttribute | undefined {
      if (!oldAttr && newAttr) return newAttr; // Added
      if (oldAttr && !newAttr) return oldAttr; // Removed
      if (oldAttr && newAttr && oldAttr.value !== newAttr.value) return newAttr; // Changed value
      return undefined;
    }

    const oldDefaults = v6Util().getQuoteDefaults(poldDefaults) ?? [];
    const newDefaults = v6Util().getQuoteDefaults(pnewDefaults) ?? [];

    const oldGroup = oldDefaults.find(x => x.groupType === FrameAttributeGroupType.Quote);
    const newGroup = newDefaults.find(x => x.groupType === FrameAttributeGroupType.Quote);

    // If neither group exists, nothing to compare
    if (!oldGroup && !newGroup) return [];

    // If only one exists, treat all attributes as changed
    if (!oldGroup && newGroup) return [...newGroup.attributes];
    if (oldGroup && !newGroup) return [...oldGroup.attributes];

    // Both exist, compare attributes
    const changedCodes = new Set<string>();
    const changedAttributes: FrameAttribute[] = [];

    if (oldGroup && newGroup) {
      // Check for changes/removals in oldGroup
      for (const oldAttr of oldGroup.attributes) {
        const newAttr = newGroup.attributes.find(a => a.code === oldAttr.code);
        const changed = getChangedAttribute(oldAttr, newAttr);
        if (changed && !changedCodes.has(changed.code)) {
          changedAttributes.push(changed);
          changedCodes.add(changed.code);
        }
      }

      // Check for additions in newGroup
      for (const newAttr of newGroup.attributes) {
        const oldAttr = oldGroup.attributes.find(a => a.code === newAttr.code);
        const changed = getChangedAttribute(oldAttr, newAttr);
        if (changed && !changedCodes.has(changed.code)) {
          changedAttributes.push(changed);
          changedCodes.add(changed.code);
        }
      }
    }
    return changedAttributes;
  }

  async anyFrameOutOfDate(errors: string[]): Promise<boolean> {
    await this.needsQuoteItems();
    const items = this.container.items ?? [];
    let outOfDate = false;

    //no implementation yet for non-v6
    if (!this.isV6) return false;

    for (const item of items) {
      const quoteItemContainer = this.quoteItemContainer(item.id);
      outOfDate = outOfDate || this.isFrameOutOfDate(quoteItemContainer, errors);
    }
    return outOfDate;
  }

  async quoteFramesAreReadyForIssuing(errors: string[]): Promise<boolean> {
    await this.needsQuoteItems();
    const items = this.container.items ?? [];
    let success = true;

    if (items.length === 0) {
      errors.push(LangFranchiseeQuoteManager.quoteDoesNotContainAnyLineItems());
      return false;
    }

    //no implementation yet for non-v6
    if (!this.isV6) return false;

    //removed health check code. the backend can revert the issue pending if it cannot complete the task

    for (const item of items) {
      const quoteItemContainer = this.quoteItemContainer(item.id);
      if (!(isV6(quoteItemContainer) && isSupplierFrameItem(quoteItemContainer.item))) continue;

      if (isSupplierFrameItem(quoteItemContainer.item)) {
        if (this.frameHasCriticalWarnings(quoteItemContainer)) {
          errors.push(
            LangFranchiseeQuoteManager.frameHasWarningsOrErrors({
              position: this.itemPosition(quoteItemContainer.item.id)!
            })
          );
          success = false;
        }
      }
    }
    success = success && (await this.anyFrameOutOfDate(errors));
    return success;
  }

  frameHasCriticalWarnings(item: QuoteItemContainer) {
    const isQuoteItemBuyInPricesCurrent = (quoteItemContainer: QuoteItemContainer) => {
      if (this.priceValidation) return this.priceValidation?.findIndex(x => x.id === quoteItemContainer.item.id) < 0;
      else return true;
    };

    const getItemValidationType = (quoteItemContainer: QuoteItemContainer): FrameValidationType => {
      //we are currently only supporting items that are for v6
      if (!isV6(quoteItemContainer)) return FrameValidationType.nothing;

      const data = getV6FrameForContainer(quoteItemContainer);
      if (!data) {
        if (isFrame(quoteItemContainer.item)) return FrameValidationType.critical;
        return FrameValidationType.nothing;
      }

      if (this.currentStateRequiresValidation) {
        if (!isQuoteFrameVersionCurrent(quoteItemContainer)) return FrameValidationType.outOfDate;

        if (!this.isBuyInDataLocked && !isQuoteItemBuyInPricesCurrent(quoteItemContainer)) {
          return FrameValidationType.outOfDate;
        }
      }
      return rankToValidationType(highestValidationRank(data.validations));
    };
    const validation = getItemValidationType(item);

    return validation === FrameValidationType.critical || validation === FrameValidationType.outOfDate;
  }

  public async canIssueQuote(): Promise<boolean> {
    const errors: string[] = [];

    // Deferred SSI (Task 230006, §6): a batch change deferred the supplier-item/pricing recompute, so the
    // quote is stale-priced. Hard-block issue until the user runs Update Price (which clears the flag).
    if (this.ssiDeferred) {
      errors.push(LangFranchiseeQuoteManager.quotePricingMustBeUpdatedBeforeIssue());
    }

    const validUntilDate = this.quote.validUntilDate ?? (await this.calculateValidUntilDate());
    if (!this.quote.validUntilDate) {
      if (validUntilDate === null) {
        errors.push(LangFranchiseeQuoteManager.unableToIssueQuoteValidForDays());
      }
    }

    if (this.hasQuoteAnnotations) {
      const critialAnnotations = this.quoteAnnotations.filter(x => x.severity === 'critical');
      critialAnnotations.forEach(x => {
        errors.push(LangFranchiseeQuoteManager.quoteHasCriticalAnnotation({ annotation: x.content }));
      });
    }
    const projectId = await this.projectId();
    const projectManager = getProjectContainerManager(projectId);
    await projectManager.needsProject();
    const project = projectManager.project;

    if (!project) {
      //Don't kill the quote with an error
      errors.push(LangFranchiseeQuoteManager.cannotIssueQuoteWithoutProject());
    } else {
      const physicalLine1 = project.defaultAddress.line1;
      const physicalPostCode = project.defaultAddress.postcode;
      if (isEmptyOrSpace(physicalLine1)) errors.push(LangFranchiseeQuoteManager.projectDoesNotHaveStreetAddress());
      if (isEmptyOrSpace(physicalPostCode)) errors.push(LangFranchiseeQuoteManager.projectDoesNotHaveZipCode());
    }

    const quoteShippingLine1 = this.quote.shippingAddress?.line1;
    const quoteShippingPostCode = this.quote.shippingAddress?.postcode;

    if (isEmptyOrSpace(this.quote.title)) errors.push(LangFranchiseeQuoteManager.quoteDoesNotHaveTitle());

    //Getting here means we already checked if the valid until
    const validDate = serverDateTimeToLocalDateTime(validUntilDate ?? 'invalid');
    if (!validDate.isValid || (validDate.isValid && validDate < DateTime.now())) {
      errors.push(LangFranchiseeQuoteManager.validToDateMustBeInFuture({ validTo: validDate.toLocaleString() }));
    }

    if (isEmptyOrSpace(quoteShippingLine1))
      errors.push(LangFranchiseeQuoteManager.quoteShippingAddressDoesNotHaveStreetAddress());

    if (isEmptyOrSpace(quoteShippingPostCode))
      errors.push(LangFranchiseeQuoteManager.quoteShippingAddressDoesNotHaveZipCode());

    await this.quoteFramesAreReadyForIssuing(errors);

    if (this.container.items) {
      for (const item of this.container.items) {
        if (isSpecialItem(item)) {
          const issuesStrings: string[] = [];
          const itemPrice = this.container.itemPrices?.find(x => x.id === item.id);

          if (!isSupplierSpecialItem(item) && isEmptyOrSpace(item.description))
            issuesStrings.push(LangFranchiseeQuoteManager.supplierReference());

          if (itemPrice === undefined || itemPrice.singleUnitCost < 0)
            issuesStrings.push(LangFranchiseeQuoteManager.validPrice());

          if (issuesStrings.length > 0) {
            errors.push(
              LangFranchiseeQuoteManager.specialItemMustHaveBeforeQuoteCanBeIssued({
                issues: joinWithCommaAnd(issuesStrings)
              })
            );
          }
        }
      }
    }

    const supportItemErrors: string[] = [];
    await this.checkForUnresolvedSupportItems(supportItemErrors);
    errors.push(...supportItemErrors);

    if (errors.length !== 0) {
      //changed the logic here to remove the abandon and close option.
      //they can do that themselves after completing this workflow
      if (supportItemErrors.length > 0) {
        await this.interaction.showValidationsWithOptions(
          errors,
          LangFranchiseeQuoteManager.unableToIssueQuoteCorrectAndTryAgain(),
          [
            {
              label: 'Show Support Items',
              type: 'stay',
              action: () => {
                this.publishUi({ type: 'show-fragment', fragment: 'support', source: 'issue-validation' });
                return Promise.resolve();
              }
            },
            { label: LangModalValidationHandler.close(), type: 'stay' }
          ]
        );
      } else {
        await this.interaction.showValidations(
          errors,
          LangFranchiseeQuoteManager.unableToIssueQuoteCorrectAndTryAgain()
        );
      }
      return false;
    }

    return true;
  }
  async checkForUnresolvedSupportItems(errors: string[]) {
    const supportTickets = await this.needsQuoteSupport();

    if (supportTickets) {
      for (const sr of supportTickets.items.filter(
        x =>
          x.type !== BranchQuoteSupportItemType.QuoteReview &&
          x.status !== BranchQuoteSupportStatus.Cancelled &&
          x.status !== BranchQuoteSupportStatus.Resolved
      )) {
        const itemType =
          sr.type === BranchQuoteSupportItemType.EngineeredToOrder
            ? LangFranchiseeQuoteManager.specialItem()
            : LangFranchiseeQuoteManager.supportRequest();
        errors.push(
          LangFranchiseeQuoteManager.supplierItemMustBeResolvedOrDeleted({
            itemType,
            subject: sr.subject
          })
        );
      }
    }
  }

  public async addConversationToLinkedSupportTickets(
    quoteItemContainer: QuoteItemContainer,
    options: {
      event: (qic: QuoteItemContainer) => InputBoundConversationEntry[];
      other?: (qic: QuoteItemContainer) => BranchQuoteSupport[];
      modifyLinks?: (ticket: BranchQuoteSupport) => { [quoteItemId: string]: SupportQuoteItemLinkDetail } | null;
    }
  ) {
    const branchQuoteSupport = (await this.needsQuoteSupport()) ?? this.branchQuoteSupport;
    const relatedSupportTickets = branchQuoteSupport.items.filter(x => {
      return x.quoteItemLink && (x.quoteItemLink[quoteItemContainer.item.id]?.isLinked ?? false);
    });

    let fireSupportNotify = false;
    if (options.other) {
      relatedSupportTickets.push(...options.other(quoteItemContainer));
    }
    if (relatedSupportTickets.length === 0) return;
    // Use a cloned service so bulk staging does not disrupt any active editing slot in the primary service.
    const svc = this.supportTicketService.clone({ shareEvents: true, shareTicketList: true });
    try {
      for (const ticket of relatedSupportTickets) {
        const eligible = flagInSet(
          ticket.status,
          BranchQuoteSupportStatus.New ||
            BranchQuoteSupportStatus.Active ||
            BranchQuoteSupportStatus.Overdue ||
            BranchQuoteSupportStatus.ReActivated
        );
        if (!eligible) continue;

        const conversationEntries = options.event(quoteItemContainer);
        if (!conversationEntries.length) continue; // nothing to apply for this ticket
        fireSupportNotify = true;
        // Ensure the service knows about this ticket (hydrates / caches list item & session shell)
        svc.ensureTicketHydrated(ticket);

        // Apply each entry through the service so snapshot + publish pipeline (ConversationChanged) fires.
        let stagedConversation = false;
        for (const e of conversationEntries) {
          svc.addPendingConversationEntry({
            conversationEntryId: e.id,
            branchQuoteSupportId: ticket.id,
            text: e.text,
            draft: e.draft ?? false,
            attachments: (e.attachments ?? []).map(a => ({ filename: a.filename })),
            users: e.users ?? []
          });
          stagedConversation = true;
        }

        // Handle optional link modifications by staging them in draft metadata (deferred unified save).
        const modifiedLinks = options.modifyLinks?.(ticket) ?? null;
        const needLinkPersist = !!modifiedLinks;
        if (needLinkPersist) {
          svc.beginMetadataEdit(ticket.id);
          svc.updateDraftMetadata(ticket.id, { quoteItemLinks: modifiedLinks });
        }
        // Persist if we staged conversation entries or link changes, using isolated clone state.
        if (stagedConversation || needLinkPersist) {
          try {
            await svc.openTicket(ticket.id, { edit: true, forceConversationReload: false });
            await svc.saveTicket(ticket.id, { finalizeDraftEntry: true });
          } catch {
            // continue processing remaining tickets
          }
        }
      }
    } finally {
      svc.disposeClone?.();
    }
    if (fireSupportNotify && relatedSupportTickets.length > 0) {
      this.interaction.notifyInformation(
        LangFranchiseeQuoteManager.supportRequestsUpdatedDueToItemChange(relatedSupportTickets.length),
        5000
      );
    }
  }

  async saveAndUpdateQuoteItem(
    quoteItemContainer: QuoteItemContainer,
    assets: { [key: string]: AttachedBlobAsset },
    extraItems?: InputUpdateQuoteItems,
    forceSave?: boolean,
    updateLinkedSupportTickets?: boolean
  ): Promise<QuoteItemContainer> {
    const result = await super.saveAndUpdateQuoteItem(quoteItemContainer, assets, extraItems, forceSave);
    if (!updateLinkedSupportTickets) return result;
    await this.needsQuoteSupport();
    await this.addConversationToLinkedSupportTickets(quoteItemContainer, {
      event: qic => {
        //todo, if we ever support other difference data refactor into method
        const markDown = isSupplierFrameItem(qic.item)
          ? getFrameCompareAsMarkdown(
              this.makeSupportLinkDisplayValueForQuoteItemId(qic.item.id, false),
              qic.differenceReport as FrameCompareReport
            )
          : '';
        return isEmptyOrSpace(markDown)
          ? []
          : [
              {
                attachments: [],
                id: newGuid(),
                text: LangFranchiseeQuoteManager.quoteItemAttachToSupportRequestModifed(markDown),
                users: [],
                draft: false
              }
            ];
      },
      other: qic => {
        const itemlink = this.quoteItemConversationLinks.filter(x => x.quoteItemId === qic.item.id);
        return this.branchQuoteSupport.items.filter(x => itemlink.some(l => l.conversationId === x.conversationId));
      }
    });

    return result;
  }

  async saveQuote(silently?: boolean, siblings?: InputQuoteState[] | null): Promise<boolean> {
    this.quote.serviceProviderData = null; //clear out any existing data to ensure we are using the latest
    this.stageQuoteProviderDataAsset();

    try {
      this.flushCaches();
      const result = await this.api.updateQuote({
        siblingQuoteState: siblings ?? null,
        assets: this.quoteUpdateAssets,
        providerDataDifference: null,
        // Delivery batches (MRD) derived from the current grouping doc — committed with the quote.
        ...this.buildBatchSaveInputs(),
        quote: this.quote,
        quotePrice: this.quotePrice,
        numberSeed: this.numberSeed,
        stateChangeReason: this.stateChangeReason,
        quotePresentation: this.container.quotePresentation
      });

      const result1 = await this.franchiseeApi.updateBranchQuote({
        branchQuote: this.branchQuote
      });
      if (result) {
        this.quoteUpdateAssets = null;
        if (result1)
          await this.resetBranchQuote(
            result.quote!,
            result.resultFullQuotePrice!.quotePrice,
            result.quotePresentation,
            result1.branchQuote!,
            null,
            this.container.quoteProviderData
          );
        else
          await this.resetQuote(
            result.quote!,
            result.resultFullQuotePrice!.quotePrice,
            null,
            result.siblings,
            this.container.quoteProviderData
          );
        await this.fetchCache();
        this.updateItemPrices(result.resultFullQuotePrice!.itemPrices);
        this.updateItemBuyIns(result.resultFullQuotePrice!.itemBuyIns);

        if (result1 && result) {
          await this.doAfterSave();
          if (!silently) this.interaction.notifyAutoSaveSuccess();

          return true;
        }
      }
      return false;
    } finally {
      this.numberSeed = null;
    }
  }

  quoteChanged(): boolean {
    return super.quoteChanged() || !compare(this.franchiseeBackup.branchQuote, this.franchiseeContainer.branchQuote);
  }
  public get isSSIEnabled(): boolean {
    return isSupplierUsingSSI(this.quote.supplierId);
  }
  public ssiProcessor(
    itemFilter?: string[] | undefined,
    extaItems?: QuoteItemContainer[],
    pendingBatchAssignments?: ReadonlyMap<string, string | null>
  ): SSIProcessor {
    if (!isSupplierUsingSSI(this.quote.supplierId)) return new MockSSIProcessor();
    return new V6SSIProcessor(this, itemFilter, extaItems, pendingBatchAssignments);
  }

  saveQuoteCalculations(v: QCQuoteValues | null | undefined) {
    const data = this.quoteProviderData() as V6FranchiseeQuoteProviderData;
    data.quoteCalculations = v ?? undefined;
  }

  saveQuoteAnnotations(v: V6QuoteAnnotation[] | null | undefined) {
    const data = this.quoteProviderData() as V6FranchiseeQuoteProviderData;
    data.quoteAnnotations = v ?? undefined;
  }

  /**
   * Copy this quote. Pass `clearShippingAddress` true whenever the copy lands OUTSIDE a project (a
   * standalone draft): there is no project address to inherit, so it starts blank. An in-project copy
   * passes false and keeps the source's address, which the client re-derives on open — storing a blank one
   * would leave the fresh copy dirty and (with quote.ssi.trigger.shipping) reprice it unprompted.
   */
  public async makeCopy(clearShippingAddress = false): NullPromise<QuoteContainer> {
    const dateAsString = localDateToRFC3339(new Date());
    await this.needsQuote();
    let defaultQuoteTitle = `${this.quote.title} - ${dateAsString}`;
    const enteredTitle = await this.interaction.promptForText({
      title: LangFranchiseeQuoteManager.enterNewQuoteTitle(),
      label: LangFranchiseeQuoteDetailView.title(),
      value: defaultQuoteTitle,
      maxLength: 50,
      required: true
    });
    if (enteredTitle === null) return null;
    defaultQuoteTitle = enteredTitle;

    return await this.interaction.runWithSavingLock<QuoteContainer | null>(
      this.internalMakeCopy(false, undefined, defaultQuoteTitle, clearShippingAddress)
    );
  }

  public async makeAlternative(forSupplierReview = false): NullPromise<QuoteContainer> {
    return await this.internalMakeCopy(
      true,
      forSupplierReview ? QuoteAlternativeType.SupplierReview : QuoteAlternativeType.Standard
    );
  }

  public async deleteQuote(): Promise<boolean> {
    // UI-level fabrication guard: checks FabricationService directly since the UI can talk to all services.
    // CleanupQuoteFabricationData rejects if any items are assigned to fabrication jobs.
    // If all are ReadyForFabrication, it deletes the rows. If no rows exist, it succeeds.
    // A 2nd-level guard also exists: BranchQuote.lockForFabrication is set by the backend
    // (FabricationService → FranchiseeService via DI) when fabrication rows are created/removed.
    if (userDataStore.isFabricatorMode) {
      try {
        const quoteItemIds = (this.container.items ?? []).map(qi => qi.id);
        const cleanup = await getApiFactory().fabricationJob().cleanupQuoteFabricationData({
          quoteId: this.quoteId,
          quoteItemIds
        });
        if (cleanup && !cleanup.success) {
          await this.interaction.showValidations([
            LangFranchiseeQuoteManager.cannotDeleteItemsAssignedToFabricationJobs({ count: cleanup.blockingItemCount })
          ]);
          return false;
        }
      } catch {
        // Non-blocking if FabricationService is unreachable
      }
    }

    // SEQUENCED, not Promise.all: the quote delete can now be REFUSED by the server (a copy or delete
    // of this quote is already in progress — Task 229184), and a refusal must not still destroy the
    // branch-quote record and leave an orphan. The api layer reports a validation refusal by showing
    // the server's message and resolving null, so a null result is the refusal signal.
    //
    // No session lock is sent: the server's delete lock is an OPERATION-purpose row, a different key
    // from the interactive lock this flow holds, so there is nothing to promote and no self-collision
    // to avoid. See LockingConstants.Purpose.
    const deleted = await this.api.deleteQuote({
      quoteId: this.quoteId,
      reason: null
    });
    if (!deleted) return false;

    await this.franchiseeApi.deleteBranchQuote({
      branchQuoteId: this.quoteId
    });

    return true;
  }

  override async getBuyInCosts(buyInItems?: QuoteFrameBuyInItem[]): Promise<StockLookupViewExtra[] | undefined> {
    if (!buyInItems || buyInItems.length === 0) return undefined;

    const supplierId = this.quote.supplierId;

    const constructLookup = (buyin: QuoteFrameBuyInItem) => {
      const code = buyin.code;
      const libCode = buyin.extraDetails?.['LibCode'] ?? '';
      const suppCode = buyin.extraDetails?.['SuppCode'] ?? '';

      return new (class implements LookupStockItem {
        supplierId = supplierId;
        code = code;
        libCode = libCode;
        supplierCode = suppCode;
      })();
    };

    const stock = await this.franchiseeApi.lookupStock({
      lookup: buyInItems.map(x => constructLookup(x))
    });

    if (!stock) return [];

    return buyInItems.map(x => {
      const item = constructLookup(x);

      // Once V6 returns the LibCode, we need to filter on that as well
      const stockItem = stock.stockItems.find(
        y => y.code === item.code && y.supplierCode === item.supplierCode && y.supplierId === item.supplierId
      );

      if (!stockItem)
        return {
          code: item.code,
          cost: null,
          description: x.decription,
          libCode: item.libCode,
          supplierCode: item.supplierCode,
          supplierId: item.supplierId,
          version: '',
          lookup: `Length=${x.extraDetails?.['Length']};Width=${x.extraDetails?.['Width']};Height=${x.extraDetails?.['Height']}`,
          marginPercentage: null,
          calculatedGross: null
        };

      const isMetric = isSupplierUnitsMetric(item.supplierId);
      const toMetricConversion = 25.4;

      //For metric, we are storing cost per m, we need to convert that to cost per mm. For imperial, we are storing values per foot
      //and need to convert that to cost per inch (1m = 1000mm, 1ft = 12inches)
      const costlength = (stockItem.cost ?? 0) / (isMetric ? 1000 : 12);
      const costArea = (stockItem.cost ?? 0) / (isMetric ? 1000 * 1000 : 12);

      const length =
        (parseFloat(x.extraDetails?.['Length'] ?? '') || 0) * (isMetric ? toMetricConversion : 1) * costlength;
      const area =
        (parseFloat(x.extraDetails?.['Width'] ?? '') * parseFloat(x.extraDetails?.['Height'] ?? '') || 0) *
        (isMetric ? toMetricConversion * toMetricConversion : 1) *
        costArea;

      //Individual prices are stored: cost = qty * individual cost
      const each = parseFloat(x.extraDetails?.['PackSize'] ? x.quantity.toString() : '') || 0;

      return {
        code: stockItem.code,
        cost: money((length + area + each) * x.quantity, 2),
        description: stockItem.description,
        libCode: stockItem.libCode,
        supplierCode: stockItem.supplierCode,
        supplierId: stockItem.supplierId,
        version: stockItem.version,
        lookup: `Length=${x.extraDetails?.['Length'] ?? ''};Width=${
          x.extraDetails?.['Width'] ?? ''
        };Height=${x.extraDetails?.['Height'] ?? ''}`,
        marginPercentage: null,
        calculatedGross: null
      };
    });
  }

  async copyCustomItemDetailsToNewAlternateQuote(
    newQuoteContainer: FranchiseeQuoteContainer,
    customItemRequests: QuoteItem[],
    isSupplierReview: boolean
  ) {
    const m = new QuoteContainerManager(
      newQuoteContainer,
      getApiFactory().quote(),
      getApiFactory().blob(),
      this.interaction
    );
    await m.needsQuoteItems();
    const support = await this.needsQuoteSupport();
    await cache().quoteItemConversation.preFetch(customItemRequests.map(x => x.id));
    const api = getApiFactory().franchisee();
    if (m.container.items)
      for (const item of m.container.items) {
        const originalItem = customItemRequests.find(x => x.commonId === item.commonId);
        if (originalItem) {
          const conversationId = cache()
            .quoteItemConversation.getLocalData(originalItem.id)
            ?.byType(QuoteItemConversationType.EngineeredToOrder);
          const bqs = support?.items.find(
            x => x.conversationId === conversationId && x.type === BranchQuoteSupportItemType.EngineeredToOrder
          );
          if (conversationId !== undefined && bqs !== undefined) {
            await api.duplicateBranchQuoteSupport({
              branchQuoteSupportId: bqs.id,
              originalQuoteItemId: originalItem.id,
              quoteItemConversationType: QuoteItemConversationType.EngineeredToOrder,
              quoteItemId: item.id,
              branchQuoteId: m.quote.id,
              status: isSupplierReview ? bqs.status : BranchQuoteSupportStatus.Draft,
              conversationEntries: [
                {
                  attachments: [],
                  id: newGuid(),
                  text: LangFranchiseeQuoteManager.quoteConversationCopied({
                    quoteNumber: getQuoteNumberFormatted(this.quote, false),
                    position: this.itemPosition(originalItem.id)!,
                    itemTitle: firstValidString(originalItem.title, LangFranchiseeQuoteManager.specialItem())
                  }),
                  users: [],
                  draft: false
                }
              ]
            });
          }
        }
      }
  }

  getPricingModelLabel(): string {
    if (this.isPricingMetricLockedBySupplier) {
      return LangFranchiseeQuoteManager.salesCommissionCalculationLabel({
        calculationLabel: this.pricingModel.calculationLabel
      });
    }

    return LangFranchiseeQuoteManager.defaultQuoteCalculationLabel({
      calculationLabel: this.pricingModel.calculationLabel
    });
  }

  protected async setupProject() {
    await userDataStore.loadCoreDetails();
    const resultClient = await getApiFactory().client().getClient({
      clientId: this.branchQuote.clientId
    });
    let clientAddress: Address | undefined = undefined;
    if (resultClient?.client) clientAddress = resultClient.client.physicalAddress;

    const addressManager = new FranchiseeQuoteAddressManager(this);
    const addressType = addressManager.getAddressTypeByTag(this.quote.shippingAddress?.source);
    let shippingNotes: string | null = null;
    // we only want to use the client or other address
    const addressToUse = () => {
      if (
        addressType === FranchiseeQuoteAddressManagerType.client ||
        addressType === FranchiseeQuoteAddressManagerType.other
      ) {
        shippingNotes = this.quote.shippingNotes;
        return determineProjectShippingAddress(
          this.quote.shippingAddress,
          true,
          clientAddress,
          resultClient?.client.shipToPhysicalAddress
        );
      }
      return emptyAddress();
    };

    const projectApi = getApiFactory().project();

    const projectSetup = await this.franchiseeInteraction.setupProject({
      clientId: this.branchQuote.clientId,
      title: this.quote.title,
      description: this.quote.description,
      defaultAddress: addressToUse(),
      shippingNotes: shippingNotes,
      budget: this.quote.budget
    });

    if (projectSetup) {
      //When quote is changed to Active, we need to generate a quote number;
      this.generateQuoteNumberOnSave();

      if (projectSetup.needsProjectCreate) {
        const project = await projectApi.createProject({
          project: {
            state: ProjectState.Active,
            title: projectSetup.projectTitle,
            number: 0,
            budget: projectSetup.budget ?? 0,
            id: newGuid(),
            clientId: this.branchQuote.clientId,
            contactId: this.branchQuote.contactId,
            clientTypeId: this.branchQuote.clientTypeId,
            termsAndConditionsId: null,
            description: projectSetup.description,
            recordVersion: '',
            dateCreated: localDateTimeToServer(today()),
            lastModifiedDate: localDateTimeToServer(today()),
            shippingNotes: projectSetup.shippingNotes,
            defaultAddress: projectSetup.projectAddress,
            shipToDefaultAddress: false,
            projectOwnerId: this.quote.quoteOwnerId,
            creationUserId: emptyGuid, //set on server
            lastModifiedUserId: emptyGuid, //set on server
            assignedToUserId: emptyGuid //set on server
          },
          numberSeed: this.quote.quoteOwnerId,
          projectReferences: [
            {
              resourceId: this.quote.id,
              typeOf: ResourceType.Quote,
              parentResourceId: emptyGuid,
              parentTypeOf: ResourceType.None
            }
          ]
        });

        if (project) {
          await cache().project.updateLocal(project.project.id);

          this.interaction.notifySuccess(
            LangFranchiseeQuoteManager.projectCreated({
              projectNumber: getProjectNumberFormatted(project.project)
            }),
            1000
          );
          return true;
        }
      } else {
        if (projectSetup.project) {
          const result = await projectApi.createProjectResourceReference({
            parentResourceId: emptyGuid,
            parentTypeOf: ResourceType.None,
            projectId: projectSetup.project.id,
            resourceId: this.quote.id,
            typeOf: ResourceType.Quote
          });
          if (result) {
            this.interaction.notifySuccess(
              LangFranchiseeQuoteManager.quoteLinkedToExistingProject({
                projectNumber: getProjectNumberFormatted(projectSetup.project)
              }),
              1000
            );
            return true;
          }
        }
      }
    }

    return false;
  }

  protected async sendIssueQuote(): Promise<boolean> {
    if (this.isV6) {
      const projectId = await this.projectId();
      if (await V6SupplierQuoteBase.issueQuote(this.branchQuote?.branchId ?? emptyGuid, this.quote.id, projectId)) {
        this.interaction.notifyInformation(LangFranchiseeQuoteManager.emailedWhenReady());
      } else return false;
    }
    return true;
  }

  private _supplierQuoteConfig: ResultGetSupplierQuoteSettings | null = null;
  private _supplierPricingConfig: ResultGetSupplierPricingRule | null = null;

  async getSupplierPricingConfig(): NullPromise<ResultGetSupplierPricingRule> {
    if (!this._supplierPricingConfig) {
      this._supplierPricingConfig = await getApiFactory().supplier().getSupplierPricingRule({
        supplierId: this.quote.supplierId
      });
    }
    return this._supplierPricingConfig;
  }

  async getSupplierQuoteConfig(): NullPromise<ResultGetSupplierQuoteSettings> {
    if (!this._supplierQuoteConfig) {
      this._supplierQuoteConfig = await getApiFactory().supplier().getSupplierQuoteConfig({
        supplierId: this.quote.supplierId
      });
    }
    return this._supplierQuoteConfig;
  }

  protected async calculateValidUntilDate(): NullPromise<string> {
    const qc = await this.getSupplierQuoteConfig();
    if (qc !== null) {
      return localDateToServer(today(qc.validityPeriod));
    }
    return null;
  }

  public async updatePricingRules() {
    const supplierPricingConfig = await this.getSupplierPricingConfig();

    const isMargin = this.quotePrice.calculation === QuotePriceCalculation.Margin;
    this.pricingModel.supplierName = this.supplierName ?? '';
    this.pricingModel.update({ calculation: this.quotePrice.calculation, min: null, max: null });
    if (supplierPricingConfig) {
      const pricingRule = supplierPricingConfig.pricingRule;

      if (supplierPricingConfig.pricingRuleType.hasMinMax)
        this.pricingModel.update({
          min: isMargin ? pricingRule.minValue : pricingRule.minMarkup,
          max: isMargin ? pricingRule.maxValue : pricingRule.maxMarkup
        });
    }
  }

  protected async isSupplierReviewEnabled(): Promise<boolean> {
    // In fabricator mode, the fabricator IS the supplier — skip supplier review
    if (userDataStore.isFabricatorMode) return false;
    return (await this.getSupplierQuoteConfig())?.allowSupplierReview ?? true;
  }

  private getQuoteFinalStateReasonAsOptions(): QuoteReasonOption[] {
    // Exclude None (0) and only include valid enum values
    return [
      QuoteFinalStateReason.Price,
      QuoteFinalStateReason.Deadline,
      QuoteFinalStateReason.ProductNotSuitable,
      QuoteFinalStateReason.CompetitorWin,
      QuoteFinalStateReason.UnresponsiveClient,
      QuoteFinalStateReason.IndecisiveClient,
      QuoteFinalStateReason.UnhappyClient,
      QuoteFinalStateReason.DifficultClient,
      QuoteFinalStateReason.Superseded,
      QuoteFinalStateReason.Other
    ].map(reason => ({
      id: reason,
      reason: this.quoteStateReasonDisplayString(reason),
      requiresComment: reason === QuoteFinalStateReason.Other
    }));
  }

  public quoteStateReasonDisplayString(reason: QuoteFinalStateReason): string {
    switch (reason) {
      case QuoteFinalStateReason.Price:
        return LangQuoteStateReasons.reason.price();
      case QuoteFinalStateReason.Deadline:
        return LangQuoteStateReasons.reason.deadline();
      case QuoteFinalStateReason.ProductNotSuitable:
        return LangQuoteStateReasons.reason.productNotSuitable();
      case QuoteFinalStateReason.CompetitorWin:
        return LangQuoteStateReasons.reason.competitorWin();
      case QuoteFinalStateReason.UnresponsiveClient:
        return LangQuoteStateReasons.reason.unresponsiveClient();
      case QuoteFinalStateReason.IndecisiveClient:
        return LangQuoteStateReasons.reason.indecisiveClient();
      case QuoteFinalStateReason.UnhappyClient:
        return LangQuoteStateReasons.reason.unhappyClient();
      case QuoteFinalStateReason.DifficultClient:
        return LangQuoteStateReasons.reason.difficultClient();
      case QuoteFinalStateReason.Superseded:
        return LangQuoteStateReasons.reason.superceeded();
      case QuoteFinalStateReason.Other:
        return LangQuoteStateReasons.reason.other();
      default:
        return '';
    }
  }

  protected async canRejectOrCancel(state: QuoteState) {
    // UI-level fabrication guard — same approach as deleteQuote.
    // A 2nd-level guard also exists via BranchQuote.lockForFabrication (see deleteQuote comment).
    if (state === QuoteState.Cancelled && userDataStore.isFabricatorMode) {
      try {
        const quoteItemIds = (this.container.items ?? []).map(qi => qi.id);
        const cleanup = await getApiFactory().fabricationJob().cleanupQuoteFabricationData({
          quoteId: this.quoteId,
          quoteItemIds
        });
        if (cleanup && !cleanup.success) {
          await this.interaction.showValidations([
            LangFranchiseeQuoteManager.cannotCancelItemsAssignedToFabricationJobs({ count: cleanup.blockingItemCount })
          ]);
          return false;
        }
      } catch {
        // Non-blocking if FabricationService is unreachable
      }
    }

    const errors: string[] = [];
    await this.checkForUnresolvedSupportItems(errors);

    if (errors.length !== 0) {
      const value = QuoteState[state].toLowerCase();
      const text = glossary(`quote-state-${value}:verb`);
      //changed the logic here to remove the abandon and close option.
      //they can do that themselves after completing this workflow
      await this.interaction.showValidations(
        errors,
        LangFranchiseeQuoteManager.cannotRejectOrCancelQuoteWithUnresolvedSupport({ state: text })
      );
      return false;
    }

    /**
     * Attempts to collect a user provided reason for changing the quotes state.
     * If there are no available reasons for a given state change we will skip asking for the reason.
     * If there are reasons available, but the user declines to given one, it will abort the state change.
     * @param quoteState The auote state we are trying to move to.
     * @param modalTitle The title of the reason modal.
     * @param modalMessage
     * @param confirmButtonText
     * @returns True if a reason was provided, or if no reasons are available. False if reasons are available but no reason whas selected.
     */
    const tryGetUserProvidedStateChangeReason = async (
      modalTitle: string,
      modalMessage: string,
      reasonSelectLabel: string,
      confirmButtonText: string
    ): Promise<boolean> => {
      const stateReasons = this.getQuoteFinalStateReasonAsOptions();
      if (stateReasons) {
        const reasonResult = await this.interaction.confirmWithReason({
          options: stateReasons,
          title: modalTitle,
          message: modalMessage,
          selectReasonLabel: reasonSelectLabel,
          confirmButtonText
        });
        if (reasonResult) {
          this.stateChangeReason = {
            comment: reasonResult.comment,
            finalStateReason: reasonResult.reasonId
          };
          return true;
        }
        return false;
      }
      return true;
    };

    const isCancelled = state === QuoteState.Cancelled;
    const quoteStatusText = LangFranchiseeQuoteManager.cancelOrRejectQuote(isCancelled);
    const modalMessage = LangFranchiseeQuoteManager.pleaseSelectReasonForStatusAction(isCancelled);
    const selectReasonLabel = LangFranchiseeQuoteManager.statusReason(isCancelled);

    return await tryGetUserProvidedStateChangeReason(quoteStatusText, modalMessage, selectReasonLabel, quoteStatusText);
  }

  protected async quoteStateBeforeChange(state: QuoteState, original: QuoteState): Promise<boolean> {
    const allowSupplierReview = await this.isSupplierReviewEnabled();
    //At this point in time, the quote state has not been edited.
    //TODO -> Need to pass in the allow/use supplier review process
    const stateHandler = getQuoteStateEngine(this, allowSupplierReview);
    if (stateHandler.isStateChangeBlocked(state)) return false;

    switch (state) {
      case QuoteState.Active:
        if (original === QuoteState.Draft) {
          if (!(await this.isQuoteValid(QuoteState.Active))) return false;
          if (!(await this.setupProject())) return false;
        }
        break;
      case QuoteState.Approved:
        // Fabricators ARE the supplier — no purchase order needed
        if (!userDataStore.isFabricatorMode) {
          //TODO-new state flow will add that we can create a purchase order that is not in draft state.
          //but already accepted. need to control that flow
          if (!(await this.createPurchaseOrder())) return false;
        }
        break;
      case QuoteState.IssuePending:
        if (!(await this.canIssueQuote())) return false;
        this.quote.validUntilDate = await this.calculateValidUntilDate();
        break;
      case QuoteState.Cancelled:
      case QuoteState.Rejected:
        if (!(await this.canRejectOrCancel(state))) return false;
        break;
      case QuoteState.SupplierReviewed:
        //dont set the valid date here. this can be done when its re-issued
        //        if (!(await this.calculateValidUntilDate())) return false;
        break;
      default:
        return true;
    }

    return true;
  }
  makeSupportLinkDisplayValueForQuoteItemId(id: string, asListItem = true): string {
    try {
      const item = this.quoteItemContainer(id);

      const prefix = asListItem ? '- ' : '';
      return isEmptyOrSpace(item.item.description)
        ? `${prefix}\\#${this.itemPosition(id)}. ${item.item.title}`
        : `${prefix}\\#${this.itemPosition(id)}. ${item.item.title} (${item.item.description})`;
    } catch {
      //item doesn't exist
      return '';
    }
  }
  protected async doBeforeDeleteQuoteItem(quoteItemContainer: QuoteItemContainer) {
    if (isSpecialItem(quoteItemContainer.item)) {
      return;
    }
    await this.addConversationToLinkedSupportTickets(quoteItemContainer, {
      event: qic => {
        //todo, if we ever support other difference data refactor into method
        const markDown = this.makeSupportLinkDisplayValueForQuoteItemId(qic.item.id, false);

        return isEmptyOrSpace(markDown)
          ? []
          : [
              {
                attachments: [],
                id: newGuid(),
                text: LangFranchiseeQuoteManager.quoteItemAttachToSupportRequestDeleted(markDown),
                users: [],
                draft: false
              }
            ];
      },
      modifyLinks: ticket => {
        if (ticket.quoteItemLink?.[quoteItemContainer.item.id]) {
          const updated = { ...ticket.quoteItemLink };
          updated[quoteItemContainer.item.id].isLinked = false;
          return updated;
        }
        return null;
      }
    });
  }

  protected async doAfterDeleteQuoteItem(quoteItemContainer: QuoteItemContainer) {
    //dont delete the QuoteITemConversationLink now that we use soft delete
    if (isSpecialItem(quoteItemContainer.item)) {
      const ticketId = quoteItemContainer.item.id;
      if (
        await this.supportTicketService.cancelSpecialItemSupportTicket({
          ticketId,
          reasonText: LangFranchiseeQuoteManager.cancelledDueToSpecialItemDeletion()
        })
      )
        await this.doAfterSave();
    }
  }
  protected async doAfterUndeleteQuoteItem(quoteItemContainer: QuoteItemContainer) {
    //dont delete the QuoteITemConversationLink now that we use soft delete
    if (isSpecialItem(quoteItemContainer.item)) {
      const ticketId = quoteItemContainer.item.id;
      if (
        await this.supportTicketService.reactivateSpecialItemSupportTicket({
          ticketId,
          reasonText: LangFranchiseeQuoteManager.reactivatedDueToSpecialItemUndeletion()
        })
      )
        await this.doAfterSave();
    }
  }

  protected async quoteStateAfterChange(_state: QuoteState, _original: QuoteState): Promise<boolean> {
    // Quote review ticket status/relocation now encapsulated in SupportTicketService.updateQuoteReviewTicket
    const resolveTickets = async () => {
      await this.supportTicketService.resolveAllTickets({
        quoteSetId: this.quote.quoteSetId,
        resolutionReason: LangFranchiseeQuoteManager.supportRequestResolvedBecauseQuoteAccepted({
          quoteNumber: getQuoteNumberFormatted(this.quote)
        })
      });
    };

    let newQuoteContainer: QuoteContainer | null = null;

    switch (_state) {
      case QuoteState.Rejected:
      case QuoteState.Cancelled:
        if (
          flagInSet(
            _original,
            // eslint-disable-next-line no-bitwise
            QuoteState.Accepted | QuoteState.Accepted_AssignedToReviewer | QuoteState.SupplierReviewPending
          )
        ) {
          //If we are here we need to cancel a support review ticket.
          //TODO#- should actually reject all open support tickets
          //for now we block cancelling if there are any open support tickets
          //we need a batch update if we want to cancel all.
          await this.supportTicketService.updateQuoteReviewTicket({ status: BranchQuoteSupportStatus.Cancelled });
        }
        return true;
      case QuoteState.SupplierReviewed:
        await this.supportTicketService.updateQuoteReviewTicket({ status: BranchQuoteSupportStatus.Resolved });
        return true;
      case QuoteState.Approved:
        await this.supportTicketService.updateQuoteReviewTicket({ status: BranchQuoteSupportStatus.Resolved });
        // Supplier approved or non-supplier quote - just resolve all tickets without creating review
        await resolveTickets();
        return true;

      case QuoteState.SupplierReviewPending:
        newQuoteContainer = await this.makeAlternative(true);
        if (newQuoteContainer) {
          await getApiFactory()
            .project()
            .createProjectResourceReference({
              resourceId: newQuoteContainer.quote?.id ?? emptyGuid,
              typeOf: ResourceType.Quote,
              parentResourceId: emptyGuid,
              parentTypeOf: ResourceType.None,
              projectId: await this.projectId()
            });
          // Relocate existing quote review ticket (if any) to new alternate quote id while keeping status unchanged.
          await this.supportTicketService.updateQuoteReviewTicket({
            status: BranchQuoteSupportStatus.New, // retain or ensure initial status if needed
            branchQuoteId: newQuoteContainer.quoteId
          });
          await monitorQuoteCopyCompletion(newQuoteContainer.quoteId);
          //goURL(resourceQuote, newQuoteContainer.quoteId);
          return true;
        }
        break;
      case QuoteState.IssuePending:
        //moved to after change, because we must be in issuepending
        //before the azure function processes this message
        if (!(await this.sendIssueQuote())) return false;
        break;
      case QuoteState.Accepted:
        //TODO.. This is not triggering when supplier doe
        //Supplier Approved was set if this has been through a review process, so that we
        //dont come back and create another support ticket
        if (!this.quote.supplierApproved && this.isSupplierQuote) {
          await this.supportTicketService.resolveAllAndCreateQuoteReviewTicket({
            quoteSetId: this.quote.quoteSetId,
            branchQuoteId: this.branchQuote.id,
            subject: LangFranchiseeQuoteManager.supplierReview(),
            resolutionReason: LangFranchiseeQuoteManager.supportRequestResolvedBecauseQuoteAccepted({
              quoteNumber: getQuoteNumberFormatted(this.quote)
            })
          });
        } else {
          // Supplier approved or non-supplier quote - just resolve all tickets without creating review
          await resolveTickets();
        }
    }
    return true;
  }

  getV6QuoteAttributesConfig(): V6QuoteAttributesConfig {
    if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
      return { triggers: [] };
    }
    if (!this.quote?.supplierId) {
      return { triggers: [] };
    }
    const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
    const customOptions = supplierOptions?.customOptions;
    if (!customOptions || Object.keys(customOptions).length === 0) {
      return { triggers: [] };
    }
    const featureFlag = 'quote.attributes.config';
    if (!(featureFlag in customOptions)) {
      return { triggers: [] };
    }
    return customOptions[featureFlag] as V6QuoteAttributesConfig;
  }

  quoteAttributesRequireSSIRecalculation(): boolean {
    if (!isSupplierUsingSSI(this.quote.supplierId)) return false;
    const result = super.quoteAttributesRequireSSIRecalculation();
    if (result) return result;

    if (!v6SupportsVersion(v6VersionMap.hasCustomOptions)) {
      return false;
    }

    if (!this.quote?.supplierId) {
      return false;
    }

    const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
    const customOptions = supplierOptions?.customOptions;

    if (!customOptions || Object.keys(customOptions).length === 0) {
      return false;
    }

    const featureFlag = 'quote.attributes.config';
    if (!(featureFlag in customOptions)) {
      return false;
    }

    const quoteData = (this.quoteProviderData() as V6FranchiseeQuoteProviderData)?.defaultOptions as object | undefined;
    const quoteBackupData = (this.backup.quoteProviderData as V6FranchiseeQuoteProviderData)?.defaultOptions as
      | object
      | undefined;

    const comparedData = this.quoteDefaultsComparison(quoteData, quoteBackupData);

    const quoteAttributeConfig = customOptions[featureFlag] as V6QuoteAttributesConfig;
    if (quoteAttributeConfig?.triggers && quoteAttributeConfig.triggers.length > 0) {
      // Check if any trigger requires SSI recalculation
      for (const trigger of quoteAttributeConfig.triggers) {
        if (trigger.runssi) {
          if (comparedData.find(x => x.code === trigger.code)) return true;
          continue;
        }
      }
    }

    return false;
  }

  protected async copyQuoteItemBefore(quoteItemContainer: QuoteItemContainer): Promise<boolean> {
    if (!isSupplierUsingSSI(this.quote.supplierId)) {
      return true;
    }
    this.copyProgress = await this.interaction.beginProgress(
      LangFranchiseeQuoteManager.copyingItemTitle({ title: quoteItemContainer.item.title }),
      3
    );

    return true;
  }

  protected async copyQuoteItemFinally(
    _quoteItemContainer: QuoteItemContainer,
    _newQuoteItemContainer: QuoteItemContainer | null
  ): Promise<void> {
    await this.copyProgress?.close();
    this.copyProgress = undefined;
  }

  protected async copyQuoteItemAfter(
    _quoteItemContainer: QuoteItemContainer,
    newQuoteItemContainer: QuoteItemContainer
  ): Promise<boolean> {
    if (newQuoteItemContainer) {
      await this.runAndSaveSSI();
    }
    return true;
  }

  protected async getSupportReview(): NullPromise<BranchQuoteSupport> {
    const support = await this.needsQuoteSupport();
    if (!support || !Array.isArray(support.items)) return null;
    const quoteReviewItems = support.items.filter(item => item.type === BranchQuoteSupportItemType.QuoteReview);
    return this.getLatestItem(quoteReviewItems);
  }

  protected async internalMakeCopy(
    asAlternate = false,
    alternativeType = QuoteAlternativeType.Standard,
    defaultQuoteTitle?: string,
    clearShippingAddress = false
  ): NullPromise<QuoteContainer> {
    await this.needsQuote();
    await this.needsQuoteItems();
    if (!this.branchQuote || !this.quote) return null;

    if (this.numberSeed && this.quoteState === QuoteState.Draft) this.quoteState = QuoteState.Active;

    //we need to remove special items from the list as ETO are not useful in a copy.
    const specialOrderItemIds =
      this.container.items
        ?.filter(item => {
          return isSpecialItem(item);
        })
        .map(item => item.id) ?? [];

    try {
      if (!asAlternate) this.quote.quoteSetId = newGuid();
      const dateAsString = localDateToRFC3339(new Date());
      defaultQuoteTitle ??= `${this.quote.title} - ${dateAsString}`;

      const quoteResult = await this.api.duplicateQuote({
        excludedQuoteItems: asAlternate ? null : specialOrderItemIds,
        quoteId: this.quote.id,
        asAlternate: asAlternate,
        // Only an out-of-project copy starts with a blank address; in-project copies keep the source's
        // (which the client re-derives on open) so the fresh copy is not dirtied. Never for an alternate.
        clearShippingAddress: !asAlternate && clearShippingAddress,
        alternativeType: asAlternate ? alternativeType : QuoteAlternativeType.Standard,
        validUntilDate: localDateToServer(today(28)),
        //Alternates use the existing number, seeds are only used by new quotes
        numberSeed: asAlternate ? null : this.numberSeed,
        quoteCustomerId: asAlternate ? this.quote.quoteCustomerId : null,
        title: asAlternate ? `${this.quote.title}` : defaultQuoteTitle,
        quoteState: !asAlternate
          ? this.numberSeed
            ? QuoteState.Active
            : QuoteState.Draft
          : alternativeType === QuoteAlternativeType.Standard
            ? QuoteState.Active
            : QuoteState.SupplierReviewPending,

        // THE HANDOVER. When this manager is the open editor (requesting supplier review copies the
        // very quote being held), the server releases this session's edit lock before deciding whether
        // the copy may proceed — so we stand down rather than being granted an exception to the rule.
        //
        // It is an INSTRUCTION, not a claim of ownership: release matches the owner signature only, so
        // if this session does not actually hold the lock nothing is freed and the copy is then
        // refused. See InputDuplicateQuote.SessionLock.
        //
        // The client half is standDownFromEditing() below — without it our own refresh loop simply
        // re-takes the lock within ~60s and the stand-down means nothing.
        sessionLock: getSessionLock()
      });

      // Stand down locally the moment the server has accepted the copy: drop our ResourceLocker so it
      // cannot re-acquire, which also flips this view read-only (release() sets isLockOwner false by
      // design). Only when we were the holder — a list/project copy holds no lock and must not disturb
      // whatever else the page is doing.
      if (quoteResult) await this.standDownFromEditing?.();

      if (quoteResult) {
        //updating a copy may have changed properties of the existing quote
        if (this.container.quote) Object.assign(this.container.quote, quoteResult.source);
        this.backup.quote = clone(this.container.quote);

        const newBranchQuote = clone(this.branchQuote);
        newBranchQuote.dateCreated = localDateTimeToServer(new Date());

        //if the new quote is a draft quote, remove the client & contact information
        if (isQuoteDraft(quoteResult.quote.state)) {
          newBranchQuote.clientId = emptyGuid;
          newBranchQuote.clientName = '';
          newBranchQuote.contactId = emptyGuid;
          newBranchQuote.contactName = '';
          newBranchQuote.clientTypeId = emptyGuid;
        }

        newBranchQuote.id = quoteResult.quote.id;
        newBranchQuote.quoteSetId = quoteResult.quote.quoteSetId;

        const branchQuoteResult = await this.franchiseeApi.createBranchQuote({
          branchQuote: newBranchQuote
        });

        if (!branchQuoteResult) {
          // Rollback delete of the quote we just created. This is the call site that made sending a
          // session lock actively harmful: nobody has this brand-new quote open, so the server's old
          // "a session lock means the caller holds the lock" inference was wrong here, and a failed
          // rollback fabricated an interactive lock on it that nothing would ever release.
          await this.api.deleteQuote({
            quoteId: quoteResult.quote.id,
            reason: null
          });
          return null;
        }

        //we need to move the support ticket to this new item if it exists
        if (asAlternate && alternativeType === QuoteAlternativeType.SupplierReview) {
          // Relocate existing QuoteReview support ticket to the new alternate quote using encapsulated service logic.
          await this.supportTicketService.relocateQuoteReviewTicket(newBranchQuote.id);
        }

        const container = new FranchiseeQuoteContainer(
          quoteResult.quote.id,
          quoteResult.quote,
          quoteResult.quotePrice,
          quoteResult.quotePresentation, //QUOTE PRESENTATION SHOULD BE HERE
          quoteResult.siblings,
          null,
          null,
          null,
          null,
          branchQuoteResult.branchQuote
        );

        //Draft quotes (thus a draft copy) cannot have a support ticket or ETO
        if (asAlternate && specialOrderItemIds.length > 0)
          await this.copyCustomItemDetailsToNewAlternateQuote(
            container,
            this.container.items?.filter(item => {
              return isSpecialItem(item);
            }) ?? [],
            alternativeType === QuoteAlternativeType.SupplierReview
          );

        return container;
      }
    } finally {
      this.numberSeed = null;
    }
    return null;
  }

  protected flushCaches() {
    const siblingIds = this.siblings?.map(x => x.quoteId) ?? [];
    cache().quote.flush([this.quoteId, ...siblingIds]);
    cache().projectResourceLink.flush([this.quoteId, ...siblingIds]);
  }

  protected internalIsReadonly(): boolean {
    const stateHandler = getQuoteStateEngine(this);

    const canEdit = stateHandler.isQuoteEditable();

    return !canEdit;
  }

  protected async resetBranchQuote(
    quote: Quote,
    quotePrice: QuotePrice,
    quotePresentation: QuotePresentation | null,
    branchQuote: BranchQuote,
    siblings: QuoteSetSibling[] | null,
    providerData: QuoteProviderData | null
  ) {
    await this.resetQuote(quote, quotePrice, quotePresentation, siblings, providerData);
    this.franchiseeContainer.branchQuote = branchQuote;
    this.franchiseeBackup.branchQuote = this.clone(branchQuote);
  }

  private getLatestItem(items: BranchQuoteSupport[]): BranchQuoteSupport | null {
    if (items.length === 0) return null;

    items.sort((itemA, itemB) => itemB.dateCreated.localeCompare(itemA.dateCreated));
    return items[0];
  }

  get displayHeightFirst(): boolean {
    try {
      const supplierOptions = v6ConfigSupplierOptions(this.quote.supplierId);
      return !supplierOptions.widthBeforeHeight;
    } catch {
      return false;
    }
  }

  get canUpdatePricingMetric(): boolean {
    try {
      const isFeatureEnabled = this.isPricingMetricLockedBySupplier;
      const isRestrictedUser = !isCynclyStaff() && !isSupplierAgent();

      if (isFeatureEnabled) return !isRestrictedUser;

      return true;
    } catch {
      return false;
    }
  }

  private async updateV6QuoteDefaults() {
    const data = this.quoteProviderData() as V6FranchiseeQuoteProviderData;
    await v6BootLoader();
    const newData = await v6Util().refreshQuoteDefaults?.(
      this.quote.supplierId,
      data.defaultOptions as object,
      data.igu as object
    );
    if (newData !== null) data.defaultOptions = newData;
    return data.defaultOptions ?? null;
  }
}
